import React, {
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  KoraxPage,
  KoraxPageHeader
} from "../../components/KoraxUI";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "auto",
    background:
      theme.palette.type === "dark"
        ? "#080E1C"
        : "#F8FAFC",
    fontFamily: "'Inter', system-ui, sans-serif",
    ...theme.scrollbarStyles
  },

  inner: {
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 18
  },

  topActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap"
  },

  button: {
    minHeight: 38,
    padding: "8px 14px",
    borderRadius: 10,
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.07)",
    color: "#1D4ED8",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 800,
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed"
    }
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14
  },

  summaryCard: {
    padding: 17,
    borderRadius: 16,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow: "0 4px 18px rgba(15,23,42,.045)"
  },

  summaryLabel: {
    color: "#94A3B8",
    fontSize: "0.67rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".045em"
  },

  summaryValue: {
    marginTop: 8,
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "1.15rem",
    fontWeight: 900,
    wordBreak: "break-word"
  },

  card: {
    minWidth: 0,
    padding: 20,
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow: "0 4px 18px rgba(15,23,42,.045)"
  },

  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap"
  },

  cardTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.94rem",
    fontWeight: 850
  },

  cardSubtitle: {
    marginTop: 4,
    color: "#94A3B8",
    fontSize: "0.72rem",
    lineHeight: 1.5
  },

  twoColumns: {
    display: "grid",
    gridTemplateColumns:
      "repeat(2, minmax(0, 1fr))",
    gap: 16,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto"
  },

  table: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "collapse"
  },

  th: {
    padding: "10px 12px",
    textAlign: "left",
    color: "#94A3B8",
    fontSize: "0.65rem",
    fontWeight: 850,
    textTransform: "uppercase",
    letterSpacing: ".055em",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#EEF2F7"
    }`,
    whiteSpace: "nowrap"
  },

  td: {
    padding: "13px 12px",
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#334155",
    fontSize: "0.75rem",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.045)"
        : "#F1F5F9"
    }`,
    verticalAlign: "middle"
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: "0.65rem",
    fontWeight: 850,
    whiteSpace: "nowrap"
  },

  empty: {
    padding: "30px 12px",
    textAlign: "center",
    color: "#94A3B8",
    fontSize: "0.76rem",
    lineHeight: 1.6
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 12
  },

  infoItem: {
    padding: 13,
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.03)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.055)"
        : "#E8EDF5"
    }`
  },

  infoLabel: {
    color: "#94A3B8",
    fontSize: "0.65rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".04em"
  },

  infoValue: {
    marginTop: 5,
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.78rem",
    fontWeight: 800,
    wordBreak: "break-word"
  }
}));

const formatNumber = value =>
  Number(value || 0).toLocaleString("pt-BR");

const formatCurrency = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const formatDate = value => {
  if (!value) {
    return "Não informado";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo"
  });
};

const formatDateTime = value => {
  if (!value) {
    return "Não informado";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo"
  });
};

const whatsappStatus = whatsapp => {
  if (whatsapp.normalizedStatus === "connected") {
    return {
      label: "Conectado",
      color: "#15803D",
      background: "rgba(34,197,94,.11)"
    };
  }

  if (
    whatsapp.normalizedStatus ===
    "awaiting_qrcode"
  ) {
    return {
      label: "Aguardando QR Code",
      color: "#B45309",
      background: "rgba(245,158,11,.13)"
    };
  }

  if (
    whatsapp.normalizedStatus ===
    "disconnected"
  ) {
    return {
      label: "Desconectado",
      color: "#B91C1C",
      background: "rgba(239,68,68,.11)"
    };
  }

  return {
    label:
      whatsapp.status ||
      "Status não informado",
    color: "#64748B",
    background: "rgba(100,116,139,.11)"
  };
};

const subscriptionStatus = subscription => {
  const status = String(
    subscription?.status || ""
  ).toLowerCase();

  if (
    status === "active" &&
    subscription?.isActive === true
  ) {
    return {
      label: "Ativa",
      color: "#15803D",
      background: "rgba(34,197,94,.11)"
    };
  }

  if (status === "pending") {
    return {
      label: "Pendente",
      color: "#B45309",
      background: "rgba(245,158,11,.13)"
    };
  }

  if (
    status === "blocked" ||
    status === "expired"
  ) {
    return {
      label: "Bloqueada",
      color: "#B91C1C",
      background: "rgba(239,68,68,.11)"
    };
  }

  if (status === "cancelled") {
    return {
      label: "Cancelada",
      color: "#475569",
      background: "rgba(100,116,139,.11)"
    };
  }

  return {
    label: status || "Não informado",
    color: "#64748B",
    background: "rgba(100,116,139,.11)"
  };
};

const DashboardMasterCompany = () => {
  const classes = useStyles();
  const history = useHistory();
  const { companyId } = useParams();
  const { user } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuper =
    user?.super === true ||
    user?.profile === "super";

  const loadCompany = useCallback(async () => {
    if (!isSuper) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await api.get(
        `/dashboard-master/companies/${companyId}`
      );

      setData(response.data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [companyId, isSuper]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  if (loading && !data) {
    return (
      <KoraxPage className={classes.root}>
        <div className={classes.empty}>
          Carregando ficha completa da empresa...
        </div>
      </KoraxPage>
    );
  }

  if (!data) {
    return (
      <KoraxPage className={classes.root}>
        <div className={classes.empty}>
          Não foi possível carregar a empresa.
        </div>
      </KoraxPage>
    );
  }

  const company = data.company || {};
  const plan = data.legacyPlan || null;
  const users = data.users || [];
  const whatsapps = data.whatsapps || [];
  const credits = data.credits || {};
  const subscriptions = data.subscriptions || [];
  const events = data.subscriptionEvents || [];
  const webhooks = data.webhookEvents || [];
  const whatsappSummary =
    data.whatsappSummary || {};

  const currentSubscription =
    subscriptions[0] || null;

  return (
    <KoraxPage className={classes.root}>
      <div className={classes.inner}>
        <div className={classes.topActions}>
          <button
            type="button"
            className={classes.button}
            onClick={() =>
              history.push("/dashboard-master")
            }
          >
            ← Voltar ao Dashboard Master
          </button>

          <button
            type="button"
            className={classes.button}
            onClick={loadCompany}
            disabled={loading}
          >
            {loading
              ? "Atualizando..."
              : "↻ Atualizar dados"}
          </button>
        </div>

        <KoraxPageHeader
          title={company.name || "Empresa"}
          subtitle={`Ficha operacional completa • Empresa #${company.id}`}
        />

        <div className={classes.summaryGrid}>
          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Status da empresa
            </div>

            <div className={classes.summaryValue}>
              {company.status === false
                ? "Bloqueada"
                : "Ativa"}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Plano atual
            </div>

            <div className={classes.summaryValue}>
              {currentSubscription?.koraxPlanName ||
                plan?.name ||
                "Sem plano"}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Usuários
            </div>

            <div className={classes.summaryValue}>
              {formatNumber(users.length)}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              WhatsApps
            </div>

            <div className={classes.summaryValue}>
              {formatNumber(
                whatsappSummary.connected
              )}
              /
              {formatNumber(
                whatsappSummary.total
              )}{" "}
              conectados
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Saldo IA
            </div>

            <div className={classes.summaryValue}>
              {formatNumber(credits.balance)}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Créditos consumidos
            </div>

            <div className={classes.summaryValue}>
              {formatNumber(
                credits.totalConsumed
              )}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Último login
            </div>

            <div className={classes.summaryValue}>
              {formatDateTime(
                company.lastLogin
              )}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Vencimento
            </div>

            <div className={classes.summaryValue}>
              {formatDate(
                currentSubscription?.nextDueDate ||
                currentSubscription?.expiresAt ||
                company.dueDate
              )}
            </div>
          </div>
        </div>

        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div>
              <div className={classes.cardTitle}>
                Dados da empresa
              </div>

              <div className={classes.cardSubtitle}>
                Informações reais cadastradas no banco.
              </div>
            </div>
          </div>

          <div className={classes.infoGrid}>
            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                Nome
              </div>
              <div className={classes.infoValue}>
                {company.name || "Não informado"}
              </div>
            </div>

            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                E-mail
              </div>
              <div className={classes.infoValue}>
                {company.email || "Não informado"}
              </div>
            </div>

            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                Telefone
              </div>
              <div className={classes.infoValue}>
                {company.phone || "Não informado"}
              </div>
            </div>

            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                Documento
              </div>
              <div className={classes.infoValue}>
                {company.document || "Não informado"}
              </div>
            </div>

            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                Criada em
              </div>
              <div className={classes.infoValue}>
                {formatDateTime(
                  company.createdAt
                )}
              </div>
            </div>

            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                Onboarding concluído
              </div>
              <div className={classes.infoValue}>
                {company.onboardingCompletedAt
                  ? formatDateTime(
                      company.onboardingCompletedAt
                    )
                  : "Não concluído"}
              </div>
            </div>

            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                Forma de pagamento
              </div>
              <div className={classes.infoValue}>
                {company.paymentMethod ||
                  "Não informado"}
              </div>
            </div>

            <div className={classes.infoItem}>
              <div className={classes.infoLabel}>
                Recorrência
              </div>
              <div className={classes.infoValue}>
                {currentSubscription?.recurrence ||
                  company.recurrence ||
                  "Não informado"}
              </div>
            </div>
          </div>
        </div>

        <div className={classes.twoColumns}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Usuários
                </div>

                <div className={classes.cardSubtitle}>
                  Usuários reais vinculados à empresa.
                </div>
              </div>
            </div>

            {users.length === 0 ? (
              <div className={classes.empty}>
                Nenhum usuário encontrado.
              </div>
            ) : (
              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.th}>
                        Usuário
                      </th>
                      <th className={classes.th}>
                        Perfil
                      </th>
                      <th className={classes.th}>
                        Status
                      </th>
                      <th className={classes.th}>
                        WhatsApp
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {users.map(userItem => (
                      <tr key={userItem.id}>
                        <td className={classes.td}>
                          <strong>
                            {userItem.name}
                          </strong>
                          <div
                            style={{
                              marginTop: 3,
                              color: "#94A3B8",
                              fontSize: "0.65rem"
                            }}
                          >
                            {userItem.email}
                          </div>
                        </td>

                        <td className={classes.td}>
                          {userItem.profile}
                        </td>

                        <td className={classes.td}>
                          <span
                            className={classes.badge}
                            style={{
                              color: userItem.online
                                ? "#15803D"
                                : "#64748B",
                              background:
                                userItem.online
                                  ? "rgba(34,197,94,.11)"
                                  : "rgba(100,116,139,.11)"
                            }}
                          >
                            {userItem.online
                              ? "Online"
                              : "Offline"}
                          </span>
                        </td>

                        <td className={classes.td}>
                          {userItem.linkedWhatsappName ||
                            "Não vinculado"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Resumo das conexões
                </div>

                <div className={classes.cardSubtitle}>
                  Situação real dos WhatsApps cadastrados.
                </div>
              </div>
            </div>

            <div className={classes.infoGrid}>
              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Total
                </div>
                <div className={classes.infoValue}>
                  {formatNumber(
                    whatsappSummary.total
                  )}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Conectados
                </div>
                <div
                  className={classes.infoValue}
                  style={{ color: "#15803D" }}
                >
                  {formatNumber(
                    whatsappSummary.connected
                  )}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Desconectados
                </div>
                <div
                  className={classes.infoValue}
                  style={{ color: "#B91C1C" }}
                >
                  {formatNumber(
                    whatsappSummary.disconnected
                  )}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Aguardando QR
                </div>
                <div
                  className={classes.infoValue}
                  style={{ color: "#B45309" }}
                >
                  {formatNumber(
                    whatsappSummary.awaitingQrCode
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div>
              <div className={classes.cardTitle}>
                Conexões WhatsApp
              </div>

              <div className={classes.cardSubtitle}>
                Status, número, provedor e atualização de cada conexão.
              </div>
            </div>
          </div>

          {whatsapps.length === 0 ? (
            <div className={classes.empty}>
              Nenhuma conexão cadastrada.
            </div>
          ) : (
            <div className={classes.tableWrap}>
              <table
                className={classes.table}
                style={{ minWidth: 980 }}
              >
                <thead>
                  <tr>
                    <th className={classes.th}>
                      Conexão
                    </th>
                    <th className={classes.th}>
                      Número
                    </th>
                    <th className={classes.th}>
                      Status
                    </th>
                    <th className={classes.th}>
                      Provider
                    </th>
                    <th className={classes.th}>
                      Padrão
                    </th>
                    <th className={classes.th}>
                      Prompt
                    </th>
                    <th className={classes.th}>
                      Atualização
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {whatsapps.map(whatsapp => {
                    const presentation =
                      whatsappStatus(whatsapp);

                    return (
                      <tr key={whatsapp.id}>
                        <td className={classes.td}>
                          <strong>
                            {whatsapp.name ||
                              `Conexão #${whatsapp.id}`}
                          </strong>
                        </td>

                        <td className={classes.td}>
                          {whatsapp.number ||
                            "Não informado"}
                        </td>

                        <td className={classes.td}>
                          <span
                            className={classes.badge}
                            style={{
                              color:
                                presentation.color,
                              background:
                                presentation.background
                            }}
                          >
                            {presentation.label}
                          </span>
                        </td>

                        <td className={classes.td}>
                          {whatsapp.provider ||
                            "Não informado"}
                        </td>

                        <td className={classes.td}>
                          {whatsapp.isDefault
                            ? "Sim"
                            : "Não"}
                        </td>

                        <td className={classes.td}>
                          {whatsapp.promptId ||
                            "Não vinculado"}
                        </td>

                        <td className={classes.td}>
                          {formatDateTime(
                            whatsapp.updatedAt
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={classes.twoColumns}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Inteligência Artificial
                </div>

                <div className={classes.cardSubtitle}>
                  Saldo e consumo real de créditos.
                </div>
              </div>
            </div>

            <div className={classes.infoGrid}>
              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Saldo atual
                </div>
                <div className={classes.infoValue}>
                  {formatNumber(credits.balance)}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Consumo acumulado
                </div>
                <div className={classes.infoValue}>
                  {formatNumber(
                    credits.totalConsumed
                  )}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Entradas no ledger
                </div>
                <div className={classes.infoValue}>
                  {formatNumber(
                    credits.totalGranted
                  )}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Débitos no ledger
                </div>
                <div className={classes.infoValue}>
                  {formatNumber(
                    credits.totalDebited
                  )}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Incluso mensalmente
                </div>
                <div className={classes.infoValue}>
                  {formatNumber(
                    credits.monthlyIncluded
                  )}
                </div>
              </div>

              <div className={classes.infoItem}>
                <div className={classes.infoLabel}>
                  Próxima renovação
                </div>
                <div className={classes.infoValue}>
                  {formatDate(credits.renewsAt)}
                </div>
              </div>
            </div>
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Assinatura
                </div>

                <div className={classes.cardSubtitle}>
                  Dados do novo módulo financeiro.
                </div>
              </div>
            </div>

            {!currentSubscription ? (
              <div className={classes.empty}>
                Esta empresa ainda não possui assinatura no novo módulo.
              </div>
            ) : (() => {
              const status =
                subscriptionStatus(
                  currentSubscription
                );

              return (
                <div className={classes.infoGrid}>
                  <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>
                      Plano
                    </div>
                    <div className={classes.infoValue}>
                      {currentSubscription.koraxPlanName ||
                        "Não informado"}
                    </div>
                  </div>

                  <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>
                      Valor
                    </div>
                    <div className={classes.infoValue}>
                      {formatCurrency(
                        currentSubscription.koraxPlanPrice
                      )}
                    </div>
                  </div>

                  <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>
                      Status
                    </div>
                    <div className={classes.infoValue}>
                      <span
                        className={classes.badge}
                        style={{
                          color: status.color,
                          background:
                            status.background
                        }}
                      >
                        {status.label}
                      </span>
                    </div>
                  </div>

                  <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>
                      Vencimento
                    </div>
                    <div className={classes.infoValue}>
                      {formatDate(
                        currentSubscription.nextDueDate ||
                        currentSubscription.expiresAt
                      )}
                    </div>
                  </div>

                  <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>
                      Payment ID
                    </div>
                    <div className={classes.infoValue}>
                      {currentSubscription.providerSubscriptionId ||
                        "Não informado"}
                    </div>
                  </div>

                  <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>
                      Cliente Asaas
                    </div>
                    <div className={classes.infoValue}>
                      {currentSubscription.asaasCustomerId ||
                        "Não informado"}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        <div className={classes.twoColumns}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Eventos financeiros
                </div>

                <div className={classes.cardSubtitle}>
                  Eventos registrados para esta empresa.
                </div>
              </div>
            </div>

            {events.length === 0 ? (
              <div className={classes.empty}>
                Nenhum evento financeiro registrado.
              </div>
            ) : (
              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.th}>
                        Evento
                      </th>
                      <th className={classes.th}>
                        Status
                      </th>
                      <th className={classes.th}>
                        Data
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {events.slice(0, 30).map(event => (
                      <tr key={event.id}>
                        <td className={classes.td}>
                          <strong>
                            {event.event}
                          </strong>
                          <div
                            style={{
                              marginTop: 3,
                              color: "#94A3B8",
                              fontSize: "0.65rem"
                            }}
                          >
                            {event.description ||
                              "Sem descrição"}
                          </div>
                        </td>

                        <td className={classes.td}>
                          {event.status ||
                            "Não informado"}
                        </td>

                        <td className={classes.td}>
                          {formatDateTime(
                            event.createdAt
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Webhooks
                </div>

                <div className={classes.cardSubtitle}>
                  Processamentos vinculados à assinatura.
                </div>
              </div>
            </div>

            {webhooks.length === 0 ? (
              <div className={classes.empty}>
                Nenhum webhook registrado.
              </div>
            ) : (
              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.th}>
                        Evento
                      </th>
                      <th className={classes.th}>
                        Processado
                      </th>
                      <th className={classes.th}>
                        Data
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {webhooks.slice(0, 30).map(webhook => (
                      <tr key={webhook.id}>
                        <td className={classes.td}>
                          {webhook.event}
                        </td>

                        <td className={classes.td}>
                          {webhook.processed
                            ? "Sim"
                            : "Não"}
                        </td>

                        <td className={classes.td}>
                          {formatDateTime(
                            webhook.createdAt
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </KoraxPage>
  );
};

export default DashboardMasterCompany;
